import type { LibraryChannel, LibraryMessage } from "./channel";
import type { LibraryStorage } from "./storage";

import type {
  GetLibraryResult,
  LibraryErrorCode,
  LibraryMutationResult,
  LibraryRestore,
  LibraryServerSnapshot,
  MovieId,
} from "@/types/library";

import { isMovieId, isRatingValue } from "./snapshot";
import {
  INITIAL_CORE,
  INITIAL_STATE,
  derivePublicState,
  libraryReducer,
  priorEntry,
  sameMovieState,
  selectMovieState,
  type LibraryCore,
  type LibraryEvent,
  type LibraryOp,
  type LibraryState,
  type MovieLibraryState,
} from "./state";

/**
 * The client-side library store: the single source of truth for the signed-in
 * user's watchlist, watched movies and ratings.
 *
 * - Hydrates instantly from a per-user localStorage cache, then revalidates
 *   from the server (and again on focus/visibility, throttled).
 * - Applies changes optimistically and rolls them back precisely on failure.
 * - Runs changes to the same movie one after another, so rapid clicks can't
 *   race; the UI always shows the outcome of the latest click.
 * - Shares confirmed changes and fresh snapshots with other tabs.
 *
 * Framework-agnostic: React binds to it with `useSyncExternalStore`.
 */

/** The server actions the store calls (injected so tests can fake them). */
export interface LibraryServerActions {
  getLibrary(): Promise<GetLibraryResult>;
  addToWatchlist(movieId: string): Promise<LibraryMutationResult>;
  removeFromWatchlist(movieId: string): Promise<LibraryMutationResult>;
  markAsWatched(movieId: string): Promise<LibraryMutationResult>;
  unmarkAsWatched(movieId: string): Promise<LibraryMutationResult>;
  rateMovie(movieId: string, rating: number): Promise<LibraryMutationResult>;
  removeRating(movieId: string): Promise<LibraryMutationResult>;
  restoreLibraryEntry(
    movieId: string,
    entry: LibraryRestore,
  ): Promise<LibraryMutationResult>;
}

/** `network` = the request itself failed (offline, server unreachable...). */
export type MutationError = LibraryErrorCode | "network";

export type MutationOutcome =
  | {
      ok: true;
      /** What the change replaced; restore it to undo the change. */
      prior: LibraryRestore;
    }
  | { ok: false; error: MutationError };

export interface LibraryStoreOptions {
  actions: LibraryServerActions;
  storage?: LibraryStorage | null;
  /** Creates the cross-tab channel when the store connects. */
  createChannel?: () => LibraryChannel | null;
  /** Clock (ms); injectable for tests. */
  now?: () => number;
  /** Minimum time between automatic revalidations. */
  revalidateThrottleMs?: number;
}

export const DEFAULT_REVALIDATE_THROTTLE_MS = 30_000;

const EMPTY_MOVIE_STATE: MovieLibraryState = Object.freeze({
  inWatchlist: false,
  addedAt: null,
  watched: false,
  watchedAt: null,
  rating: null,
  ratedAt: null,
  isPending: false,
});

type ActionOutcome = { ok: true } | { ok: false; error: MutationError };

export class LibraryStore {
  private core: LibraryCore = INITIAL_CORE;
  private state: LibraryState = INITIAL_STATE;
  private readonly listeners = new Set<() => void>();
  private readonly movieStates = new Map<MovieId, MovieLibraryState>();
  /** Tail of each movie's queue of changes. */
  private readonly queues = new Map<MovieId, Promise<void>>();
  private readonly actions: LibraryServerActions;
  private readonly storage: LibraryStorage | null;
  private readonly createChannel: () => LibraryChannel | null;
  private readonly now: () => number;
  private readonly throttleMs: number;
  private channel: LibraryChannel | null = null;
  private seq = 0;
  /** Bumped whenever the user changes; stale async results are ignored. */
  private generation = 0;
  private syncInFlight: Promise<void> | null = null;
  private lastSyncAt = Number.NEGATIVE_INFINITY;
  /** A server snapshot that arrived before we knew who is signed in. */
  private pendingSeed: LibraryServerSnapshot | null = null;

  constructor(options: LibraryStoreOptions) {
    this.actions = options.actions;
    this.storage = options.storage ?? null;
    this.createChannel = options.createChannel ?? (() => null);
    this.now = options.now ?? Date.now;
    this.throttleMs =
      options.revalidateThrottleMs ?? DEFAULT_REVALIDATE_THROTTLE_MS;
  }

  // -- React bindings ------------------------------------------------------

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  };

  getState = (): LibraryState => this.state;

  /** What the server renders (nothing is known about the user there). */
  getServerState = (): LibraryState => INITIAL_STATE;

  /** One movie's state; the same object is returned while it is unchanged. */
  getMovieState = (movieId: MovieId): MovieLibraryState => {
    const next = selectMovieState(this.state, movieId);
    const cached = this.movieStates.get(movieId);

    if (cached && sameMovieState(cached, next)) return cached;
    this.movieStates.set(movieId, next);

    return next;
  };

  getServerMovieState = (): MovieLibraryState => EMPTY_MOVIE_STATE;

  // -- Lifecycle -----------------------------------------------------------

  /**
   * Start cross-tab sync and focus/visibility/online revalidation.
   * Returns a cleanup function.
   */
  connect(target: Window = window): () => void {
    const channel = this.createChannel();
    const unsubscribe = channel?.subscribe((message) =>
      this.handleMessage(message),
    );
    const revalidate = () => void this.revalidate();
    const onVisibilityChange = () => {
      if (target.document.visibilityState === "visible") revalidate();
    };

    this.channel = channel;
    target.addEventListener("focus", revalidate);
    target.addEventListener("online", revalidate);
    target.document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      target.removeEventListener("focus", revalidate);
      target.removeEventListener("online", revalidate);
      target.document.removeEventListener(
        "visibilitychange",
        onVisibilityChange,
      );
      unsubscribe?.();
      channel?.close();
      if (this.channel === channel) this.channel = null;
    };
  }

  /**
   * Tell the store who is signed in (`null` = nobody). Switching users never
   * shows the previous user's data; signing out clears every cache.
   */
  setUser(userId: string | null): void {
    if (userId === this.core.userId) return;

    this.generation += 1;
    this.queues.clear();
    this.movieStates.clear();
    this.syncInFlight = null;
    this.lastSyncAt = Number.NEGATIVE_INFINITY;

    if (userId === null) {
      this.pendingSeed = null;
      this.storage?.clearAll();
      this.dispatch({ type: "user", userId: null, cached: null });

      return;
    }

    this.storage?.removeOthers(userId);
    this.dispatch({
      type: "user",
      userId,
      cached: this.storage?.read(userId) ?? null,
    });

    const seed = this.pendingSeed;

    this.pendingSeed = null;
    if (seed?.userId === userId) this.adoptSnapshot(seed);

    void this.revalidate({ force: true });
  }

  /**
   * Accept a snapshot read on the server for this page (it is often newer
   * than the local cache). Ignored if it belongs to another user.
   */
  receiveSnapshot(snapshot: LibraryServerSnapshot): void {
    if (this.core.userId === undefined) {
      this.pendingSeed = snapshot;

      return;
    }
    if (snapshot.userId === this.core.userId) this.adoptSnapshot(snapshot);
  }

  /** Reload the library from the server (throttled unless `force`). */
  revalidate({ force = false }: { force?: boolean } = {}): Promise<void> {
    const userId = this.core.userId;

    if (typeof userId !== "string") return Promise.resolve();
    if (this.syncInFlight) return this.syncInFlight;

    const requestedAt = this.now();

    if (!force && requestedAt - this.lastSyncAt < this.throttleMs) {
      return Promise.resolve();
    }

    const generation = this.generation;

    this.lastSyncAt = requestedAt;
    this.dispatch({ type: "syncStart" });

    const sync = (async () => {
      let result: GetLibraryResult | null;

      try {
        result = await this.actions.getLibrary();
      } catch {
        result = null;
      }

      if (generation !== this.generation) return;

      if (!result) {
        this.dispatch({ type: "syncFailure", error: "network" });
      } else if (!result.ok) {
        this.dispatch({ type: "syncFailure", error: result.error });
      } else if (result.userId !== userId) {
        // The session changed underneath us (e.g. signed in as someone else
        // in another tab); the auth listener will switch users shortly.
        this.dispatch({ type: "syncFailure", error: "unauthenticated" });
      } else {
        const snapshot = {
          userId,
          library: result.library,
          syncedAt: result.syncedAt,
        };

        this.dispatch({
          type: "snapshot",
          ...snapshot,
          requestedAt,
          fromRequest: true,
          now: this.now(),
        });
        this.channel?.post({ type: "snapshot", ...snapshot, requestedAt });
      }
    })();

    this.syncInFlight = sync.finally(() => {
      if (generation === this.generation) this.syncInFlight = null;
    });

    return this.syncInFlight;
  }

  // -- Changes -------------------------------------------------------------

  addToWatchlist(movieId: MovieId): Promise<MutationOutcome> {
    return this.mutate({ type: "watchlist/add", movieId, at: this.iso() }, () =>
      this.actions.addToWatchlist(movieId),
    );
  }

  removeFromWatchlist(movieId: MovieId): Promise<MutationOutcome> {
    return this.mutate({ type: "watchlist/remove", movieId }, () =>
      this.actions.removeFromWatchlist(movieId),
    );
  }

  /** Also removes the movie from the watchlist. */
  markWatched(movieId: MovieId): Promise<MutationOutcome> {
    return this.mutate({ type: "watched/add", movieId, at: this.iso() }, () =>
      this.actions.markAsWatched(movieId),
    );
  }

  /** Also removes the rating (confirm with the user first). */
  unmarkWatched(movieId: MovieId): Promise<MutationOutcome> {
    return this.mutate({ type: "watched/remove", movieId }, () =>
      this.actions.unmarkAsWatched(movieId),
    );
  }

  /** Also marks the movie watched and removes it from the watchlist. */
  rate(movieId: MovieId, rating: number): Promise<MutationOutcome> {
    if (!isRatingValue(rating)) {
      return Promise.resolve({ ok: false, error: "invalid_input" });
    }

    return this.mutate(
      { type: "rating/set", movieId, rating, at: this.iso() },
      () => this.actions.rateMovie(movieId, rating),
    );
  }

  /** The movie stays watched. */
  removeRating(movieId: MovieId): Promise<MutationOutcome> {
    return this.mutate({ type: "rating/remove", movieId }, () =>
      this.actions.removeRating(movieId),
    );
  }

  /** Put back a previous state (the `prior` of an earlier change). */
  restore(movieId: MovieId, entry: LibraryRestore): Promise<MutationOutcome> {
    return this.mutate({ type: "restore", movieId, entry }, () =>
      this.actions.restoreLibraryEntry(movieId, entry),
    );
  }

  // -- Internals -----------------------------------------------------------

  private iso(): string {
    return new Date(this.now()).toISOString();
  }

  private emit() {
    for (const listener of [...this.listeners]) listener();
  }

  private dispatch(event: LibraryEvent) {
    const previous = this.core;
    const next = libraryReducer(previous, event);

    if (next === previous) return;
    this.core = next;

    if (
      this.storage &&
      typeof next.userId === "string" &&
      next.base &&
      next.syncedAt &&
      (next.base !== previous.base || next.syncedAt !== previous.syncedAt)
    ) {
      this.storage.write(next.userId, {
        library: next.base,
        syncedAt: next.syncedAt,
      });
    }

    const state = derivePublicState(next, this.state);

    if (state !== this.state) {
      this.state = state;
      this.emit();
    }
  }

  /**
   * Adopt a snapshot we didn't request ourselves. `requestedAt` (this
   * device's clock) is known for snapshots from other tabs; for server-rendered
   * ones it isn't, so all recently confirmed changes are re-applied.
   */
  private adoptSnapshot(
    { userId, library, syncedAt }: LibraryServerSnapshot,
    requestedAt: number | null = null,
  ) {
    this.lastSyncAt = this.now();
    this.dispatch({
      type: "snapshot",
      userId,
      library,
      syncedAt,
      requestedAt,
      fromRequest: false,
      now: this.now(),
    });
  }

  private handleMessage(message: LibraryMessage) {
    const userId = this.core.userId;

    if (typeof userId !== "string" || message.userId !== userId) return;

    if (message.type === "op") {
      this.seq += 1;
      this.dispatch({
        type: "remoteOp",
        userId,
        seq: this.seq,
        op: message.op,
        now: this.now(),
      });
    } else {
      this.adoptSnapshot(message, message.requestedAt);
    }
  }

  private mutate(
    op: LibraryOp,
    call: () => Promise<LibraryMutationResult>,
  ): Promise<MutationOutcome> {
    const userId = this.core.userId;

    if (typeof userId !== "string") {
      return Promise.resolve({ ok: false, error: "unauthenticated" });
    }
    if (!isMovieId(op.movieId)) {
      return Promise.resolve({ ok: false, error: "invalid_input" });
    }

    const generation = this.generation;
    const prior = priorEntry(this.state.library, op);

    this.seq += 1;
    const seq = this.seq;

    this.dispatch({ type: "opStart", seq, op });

    // Changes to the same movie run one after another.
    const previous = this.queues.get(op.movieId) ?? Promise.resolve();
    const outcome = previous.then(() =>
      this.execute(seq, op, call, generation, userId),
    );
    const tail = outcome.then(
      () => undefined,
      () => undefined,
    );

    this.queues.set(op.movieId, tail);
    void tail.then(() => {
      if (this.queues.get(op.movieId) === tail) this.queues.delete(op.movieId);
    });

    return outcome.then((result): MutationOutcome =>
      result.ok ? { ok: true, prior } : result,
    );
  }

  private async execute(
    seq: number,
    op: LibraryOp,
    call: () => Promise<LibraryMutationResult>,
    generation: number,
    userId: string,
  ): Promise<ActionOutcome> {
    // The user signed out (or switched) while this was queued.
    if (generation !== this.generation) {
      return { ok: false, error: "unauthenticated" };
    }

    let result: LibraryMutationResult | null;

    try {
      result = await call();
    } catch {
      result = null;
    }

    if (generation !== this.generation) {
      return { ok: false, error: "unauthenticated" };
    }

    if (result?.ok) {
      this.dispatch({ type: "opSuccess", seq, now: this.now() });
      this.channel?.post({ type: "op", userId, op });

      return { ok: true };
    }

    this.dispatch({ type: "opFailure", seq });
    const error: MutationError = result ? result.error : "network";

    // The server undoes partial changes itself; resync in case that failed.
    if (error === "failed") void this.revalidate({ force: true });

    return { ok: false, error };
  }
}
